const STORAGE_KEY = "esp8266_state_v1";
const STATE_VERSION = 1;
const DEFAULT_USER_ID = "newuser";
const INITIAL_MEMORY_MAXIMA = [4, 11, 18, 25, 32] as const;
const MEMORY_RANGES = [
    [5, 11],
    [12, 18],
    [19, 25],
    [26, 32],
    [33, 36],
] as const;
const MAX_USER_ID_LENGTH = 128;
const MAX_USERNAME_LENGTH = 64;
const MAX_HISTORY_ENTRIES = 256;
const MAX_HISTORY_ENTRY_LENGTH = 8192;

type MemoryMaxima = [number, number, number, number, number];

interface PersistentState {
    version: 1;
    userId: string;
    username: string;
    memoryMaxima: MemoryMaxima;
    history: string[];
}

// 実機の lastSavedFixedText と同じく、固定文の重複抑止だけに使うRAM状態。
// API会話の質問・回答は同じ内容でも履歴上の別メッセージとして保存する。
let lastSavedFixedText = "";

function defaultState(): PersistentState {
    return {
        version: STATE_VERSION,
        userId: (
            localStorage.getItem("esp8266_user_id") || DEFAULT_USER_ID
        ).slice(0, MAX_USER_ID_LENGTH),
        username: (localStorage.getItem("esp8266_username") || "").slice(
            0,
            MAX_USERNAME_LENGTH,
        ),
        memoryMaxima: [...INITIAL_MEMORY_MAXIMA],
        history: [],
    };
}

function normalizeMemoryMaxima(value: unknown): MemoryMaxima {
    if (!Array.isArray(value) || value.length !== MEMORY_RANGES.length) {
        return [...INITIAL_MEMORY_MAXIMA];
    }

    return value.map((item, index) => {
        const initial = INITIAL_MEMORY_MAXIMA[index];
        const maximum = MEMORY_RANGES[index][1];
        return typeof item === "number" && Number.isFinite(item)
            ? Math.min(maximum, Math.max(initial, Math.floor(item)))
            : initial;
    }) as MemoryMaxima;
}

function readState(): PersistentState {
    const fallback = defaultState();
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return fallback;

    try {
        const parsed = JSON.parse(raw) as Partial<PersistentState>;
        if (parsed.version !== STATE_VERSION) return fallback;
        return {
            version: STATE_VERSION,
            userId:
                typeof parsed.userId === "string" && parsed.userId.length > 0
                    ? parsed.userId.slice(0, MAX_USER_ID_LENGTH)
                    : fallback.userId,
            username:
                typeof parsed.username === "string"
                    ? parsed.username.slice(0, MAX_USERNAME_LENGTH)
                    : fallback.username,
            memoryMaxima: normalizeMemoryMaxima(parsed.memoryMaxima),
            history: Array.isArray(parsed.history)
                ? parsed.history
                      .filter(
                          (entry): entry is string => typeof entry === "string",
                      )
                      .slice(0, MAX_HISTORY_ENTRIES)
                      .map(entry => entry.slice(0, MAX_HISTORY_ENTRY_LENGTH))
                : [],
        };
    } catch {
        return fallback;
    }
}

function writeState(state: PersistentState): void {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
        localStorage.setItem("esp8266_user_id", state.userId);
        if (state.username) {
            localStorage.setItem("esp8266_username", state.username);
        } else {
            localStorage.removeItem("esp8266_username");
        }
    } catch {
        // 実機のLittleFS書込み失敗と同様、容量不足でもゲーム進行を止めない。
    }
}

export function getUserId(): string {
    return readState().userId;
}

export function setUserId(userId: string): void {
    const state = readState();
    state.userId =
        userId.trim().slice(0, MAX_USER_ID_LENGTH) || DEFAULT_USER_ID;
    writeState(state);
}

export function getUsername(): string {
    return readState().username;
}

export function setUsername(username: string): void {
    const state = readState();
    state.username = username.slice(0, MAX_USERNAME_LENGTH);
    writeState(state);
}

export function updateMemoryProgress(internalTextIndex: number): void {
    const state = readState();
    const characterIndex = MEMORY_RANGES.findIndex(
        ([start, end]) =>
            internalTextIndex >= start && internalTextIndex <= end,
    );
    if (characterIndex < 0) return;

    state.memoryMaxima[characterIndex] = Math.max(
        state.memoryMaxima[characterIndex],
        internalTextIndex,
    );
    writeState(state);
}

export function getMemoryCounts(): MemoryMaxima {
    const maxima = readState().memoryMaxima;
    return maxima.map(
        (maximum, index) => maximum - INITIAL_MEMORY_MAXIMA[index],
    ) as MemoryMaxima;
}

export function appendHistory(entry: string): void {
    const state = readState();
    if (state.history.length >= MAX_HISTORY_ENTRIES) return;
    state.history.push(entry.slice(0, MAX_HISTORY_ENTRY_LENGTH));
    writeState(state);
}

export function appendFixedHistory(entry: string): void {
    if (entry === lastSavedFixedText) return;
    appendHistory(entry);
    lastSavedFixedText = entry;
}

export function getHistoryEntry(index: number): string | null {
    if (!Number.isInteger(index) || index < 0) return null;
    return readState().history[index] ?? null;
}

export function resetGameData(): void {
    const userId = getUserId();
    writeState({
        ...defaultState(),
        userId,
        username: "",
    });
    localStorage.removeItem("esp8266_conversation_id");
}
